import { useState, useEffect, useCallback } from 'react'
import {
  getEventCities,
  createEventCity,
  updateEventCity,
  deleteEventCity,
  reorderEventCities,
  type EventCitySummary,
  type EventCityFilters,
  type EventCityCreateData,
  type EventCityUpdateData,
  type EventCityRegion,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { EventCityCreateModal } from './EventCityCreateModal'

const regionOptions: EventCityRegion[] = ['Northeast', 'Midwest', 'South', 'West']

const activeOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
]

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
      }`}
    >
      {isActive ? 'active' : 'inactive'}
    </span>
  )
}

function EventCountBadge({ count }: { count: number }) {
  if (count === 0) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400"
        title="No upcoming active events for this city"
      >
        No upcoming events
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-[#f0e3ff] px-2 py-0.5 text-xs font-medium text-[#503864] dark:bg-[#2d2236] dark:text-[#b58fcc]">
      {count} upcoming
    </span>
  )
}

function CoverThumb({ url }: { url: string | null }) {
  const [broken, setBroken] = useState(false)

  if (!url || broken) {
    return (
      <div className="flex h-10 w-16 items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-[#3d2c4f] text-[10px] text-gray-400">
        {url ? 'broken' : 'none'}
      </div>
    )
  }

  return (
    <img
      src={url}
      alt=""
      onError={() => setBroken(true)}
      className="h-10 w-16 rounded-lg object-cover"
    />
  )
}

export function EventCities() {
  const [cities, setCities] = useState<EventCitySummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<EventCityFilters>({
    is_active: '',
    region: '',
    search: '',
    page: 1,
    page_size: 50,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }))
  }, [debouncedSearch])

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [reordering, setReordering] = useState(false)

  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null)
  const [editValue, setEditValue] = useState('')

  const canEdit = usePermission('event_cities', 'edit')
  const canCreate = usePermission('event_cities', 'create')
  const canDelete = usePermission('event_cities', 'delete')
  const canReorder = usePermission('event_cities', 'reorder')

  const reorderEnabled = canReorder

  const loadCities = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getEventCities(filters)
      if (response.ok && response.data) {
        setCities(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load event cities')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadCities()
  }, [loadCities])

  const handleFilterChange = (key: keyof EventCityFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const startEditing = (id: string, field: string, currentValue: string) => {
    if (!canEdit) return
    setEditingCell({ id, field })
    setEditValue(currentValue)
  }

  const saveEdit = async () => {
    if (!editingCell) return
    const { id, field } = editingCell
    const update: EventCityUpdateData =
      field === 'name' ? { name: editValue } : { display_name: editValue }
    const response = await updateEventCity(id, update)
    if (response.ok) {
      loadCities()
    } else {
      setError(response.error?.message || 'Failed to update city')
    }
    setEditingCell(null)
  }

  const cancelEdit = () => {
    setEditingCell(null)
  }

  const handleRegionChange = async (id: string, value: string) => {
    const response = await updateEventCity(id, { region: (value as EventCityRegion) || null })
    if (response.ok) {
      loadCities()
    } else {
      setError(response.error?.message || 'Failed to update city')
    }
  }

  const handleActiveToggle = async (id: string, isActive: boolean) => {
    const response = await updateEventCity(id, { is_active: isActive })
    if (response.ok) {
      loadCities()
    } else {
      setError(response.error?.message || 'Failed to update city')
    }
  }

  const handleDelete = async (id: string) => {
    const response = await deleteEventCity(id)
    if (response.ok) {
      setDeleteConfirmId(null)
      loadCities()
    } else {
      setError(response.error?.message || 'Failed to delete city')
    }
  }

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= cities.length) return
    const next = [...cities]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    const previous = cities
    setCities(next)
    setReordering(true)
    try {
      const response = await reorderEventCities(next.map((c) => c.id))
      if (response.ok) {
        loadCities()
      } else {
        setCities(previous)
        setError(response.error?.message || 'Failed to reorder cities')
      }
    } catch {
      setCities(previous)
      setError('Failed to connect to API')
    } finally {
      setReordering(false)
    }
  }

  const handleCreate = async (data: EventCityCreateData) => {
    const response = await createEventCity(data)
    if (response.ok) {
      loadCities()
    } else {
      throw new Error(response.error?.message || 'Failed to create city')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">Event Cities</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} cities</p>
        </div>
        {canCreate && (
          <CustomButton variant="primary" onClick={() => setShowCreateModal(true)}>
            Add City
          </CustomButton>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search cities..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.region || ''}
          onChange={(e) => handleFilterChange('region', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          <option value="">All Regions</option>
          {regionOptions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select
          value={filters.is_active || ''}
          onChange={(e) => handleFilterChange('is_active', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          {activeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#503864] border-t-transparent" />
        </div>
      ) : cities.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">No cities found</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-[#3d2c4f]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#1a1520]">
                {reorderEnabled && <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Order</th>}
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Cover</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">City</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Display Name</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Region</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Events</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Position</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                {canDelete && <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {cities.map((city, index) => (
                <tr key={city.id} className="border-b border-gray-100 dark:border-[#3d2c4f] hover:bg-gray-50 dark:hover:bg-[#2d2236]">
                  {reorderEnabled && (
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMove(index, -1)}
                          disabled={index === 0 || reordering}
                          className="rounded p-1 text-gray-400 hover:text-[#7f4f9a] disabled:opacity-30 disabled:hover:text-gray-400"
                          title="Move up"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleMove(index, 1)}
                          disabled={index === cities.length - 1 || reordering}
                          className="rounded p-1 text-gray-400 hover:text-[#7f4f9a] disabled:opacity-30 disabled:hover:text-gray-400"
                          title="Move down"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  )}
                  {/* Cover */}
                  <td className="px-3 py-3">
                    <CoverThumb key={city.cover_image_url} url={city.cover_image_url} />
                  </td>
                  {/* City - editable name */}
                  <td className="px-3 py-3">
                    {editingCell?.id === city.id && editingCell?.field === 'name' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                        autoFocus
                        className="w-full rounded border border-[#503864] bg-white dark:bg-[#1a1520] px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      />
                    ) : (
                      <div>
                        <span
                          className={`block font-medium text-gray-900 dark:text-white ${canEdit ? 'cursor-pointer hover:text-[#503864] dark:hover:text-[#b58fcc]' : ''}`}
                          onDoubleClick={() => startEditing(city.id, 'name', city.name)}
                        >
                          {city.name}, {city.state_code}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{city.slug}</span>
                      </div>
                    )}
                  </td>
                  {/* Display name - editable */}
                  <td className="max-w-xs px-3 py-3">
                    {editingCell?.id === city.id && editingCell?.field === 'display_name' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                        autoFocus
                        className="w-full rounded border border-[#503864] bg-white dark:bg-[#1a1520] px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      />
                    ) : (
                      <span
                        className={`block truncate text-gray-700 dark:text-gray-300 ${canEdit ? 'cursor-pointer hover:text-[#503864] dark:hover:text-[#b58fcc]' : ''}`}
                        onDoubleClick={() => startEditing(city.id, 'display_name', city.display_name || '')}
                        title={city.display_name || ''}
                      >
                        {city.display_name || '—'}
                      </span>
                    )}
                  </td>
                  {/* Region */}
                  <td className="px-3 py-3">
                    {canEdit ? (
                      <select
                        value={city.region || ''}
                        onChange={(e) => handleRegionChange(city.id, e.target.value)}
                        className="cursor-pointer rounded-lg border border-gray-200 dark:border-[#3d2c4f] bg-transparent px-2 py-1 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      >
                        <option value="">None</option>
                        {regionOptions.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{city.region || '—'}</span>
                    )}
                  </td>
                  {/* Event count (read-only) */}
                  <td className="px-3 py-3">
                    <EventCountBadge count={city.event_count} />
                  </td>
                  {/* Position */}
                  <td className="px-3 py-3 text-gray-700 dark:text-gray-300">{city.sort_order}</td>
                  {/* Status */}
                  <td className="px-3 py-3">
                    {canEdit ? (
                      <select
                        value={city.is_active ? 'true' : 'false'}
                        onChange={(e) => handleActiveToggle(city.id, e.target.value === 'true')}
                        className="cursor-pointer rounded-full border-0 bg-transparent text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      >
                        <option value="true">active</option>
                        <option value="false">inactive</option>
                      </select>
                    ) : (
                      <ActiveBadge isActive={city.is_active} />
                    )}
                  </td>
                  {/* Actions */}
                  {canDelete && (
                    <td className="px-3 py-3">
                      {deleteConfirmId === city.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(city.id)}
                            className="text-xs font-medium text-red-600 hover:text-red-800"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(city.id)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Delete"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton onClick={() => handlePageChange(page - 1)} disabled={page <= 1} variant="secondary">
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <CustomButton onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} variant="secondary">
            Next
          </CustomButton>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <EventCityCreateModal onClose={() => setShowCreateModal(false)} onCreate={handleCreate} />
      )}
    </div>
  )
}
